import { Icon, cn, type IconName } from "@sagot/ui";
import { safeHref } from "../markdown";
import type { Citation, SourceType } from "../types";

const SOURCE_ICON: Record<SourceType, IconName> = {
  pdf: "file-text",
  docx: "file-text",
  pptx: "file-text",
  text: "file-text",
  xlsx: "file-spreadsheet",
  csv: "file-spreadsheet",
  url: "globe",
  live: "database",
  action: "database",
  handbook: "book-open",
  calendar: "calendar",
  fees: "wallet",
  memo: "scroll-text",
  image: "image",
};

/** Icon by source type, falling back to the file extension in the title. */
export function sourceIcon(citation: Pick<Citation, "source_type" | "title">): IconName {
  if (citation.source_type) return SOURCE_ICON[citation.source_type];
  if (/\.(xlsx|csv)$/i.test(citation.title)) return "file-spreadsheet";
  return "file-text";
}

export function snippetId(baseId: string): string {
  return `${baseId}-snippet`;
}

export interface CitationChipProps {
  n: number;
  citation?: Citation;
  active: boolean;
  /** Id of the snippet panel it controls. */
  controls: string;
  onToggle: (n: number) => void;
}

/** Inline `.cite` marker in the answer body. Toggles the matching source snippet. */
export function CitationChip({ n, citation, active, controls, onToggle }: CitationChipProps) {
  return (
    <button
      type="button"
      className={cn("cite", active && "bg-primary text-on-primary")}
      aria-label={citation ? `Source ${n}: ${citation.title}` : `Source ${n}`}
      aria-expanded={active}
      aria-controls={controls}
      onClick={() => {
        onToggle(n);
      }}
    >
      {n}
    </button>
  );
}

export interface SourceSnippetLabels {
  openSource: string;
  match: (score: string) => string;
  updated: (date: string) => string;
}

const DEFAULT_SNIPPET_LABELS: SourceSnippetLabels = {
  openSource: "Open source page",
  match: (score) => `Match ${score}`,
  updated: (date) => `Updated ${date}`,
};

export interface SourceSnippetProps {
  id: string;
  citation: Citation | undefined;
  /** Widget variant shows score / updated date. */
  detailed?: boolean;
  labels?: Partial<SourceSnippetLabels>;
}

/** Quoted chunk under the chips (border-l-2 primary, surface-2). Hidden when no chip is active. */
export function SourceSnippet({ id, citation, detailed = false, labels }: SourceSnippetProps) {
  const l = { ...DEFAULT_SNIPPET_LABELS, ...labels };
  const href = citation?.url ? safeHref(citation.url) : null;
  return (
    <div id={id} hidden={!citation} className="mt-2 rounded-md border-l-2 border-primary bg-surface-2 px-3 py-2 text-xs leading-relaxed text-fg-muted">
      {citation ? (
        <>
          <div className="mb-1 font-bold text-fg">
            {citation.location ? `${citation.title} · ${citation.location}` : citation.title}
          </div>
          <blockquote>“{citation.snippet}”</blockquote>
          {detailed || href ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-subtle">
              {detailed && citation.score !== undefined ? <span className="font-mono">{l.match(citation.score.toFixed(2))}</span> : null}
              {detailed && citation.updated_at ? <span>{l.updated(citation.updated_at)}</span> : null}
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary-ink hover:underline">
                  {l.openSource}
                  <Icon name="external-link" className="size-3" />
                </a>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export interface SourcesListProps {
  citations: readonly Citation[];
  active: number | null;
  onToggle: (n: number) => void;
  snippetPanelId: string;
  label?: string;
  detailed?: boolean;
  snippetLabels?: Partial<SourceSnippetLabels>;
  /**
   * Show each chip's number. Only meaningful when the answer text carries the matching inline
   * `[n]` markers ("Inline [1]" style); with sources below the text, numbers would point nowhere.
   */
  showNumbers?: boolean;
}

/** "Sources" row: chip per citation ([n ·] icon · title · location) + the shared snippet panel. */
export function SourcesList({ citations, active, onToggle, snippetPanelId, label = "Sources", detailed, snippetLabels, showNumbers = true }: SourcesListProps) {
  if (citations.length === 0) return null;
  return (
    <div>
      <ul aria-label={label} className="mt-2 flex flex-wrap gap-1.5">
        {citations.map((c) => (
          <li key={c.n} className="max-w-full">
            <button
              type="button"
              aria-expanded={active === c.n}
              aria-controls={snippetPanelId}
              onClick={() => {
                onToggle(c.n);
              }}
              className={cn(
                "inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-muted hover:border-primary hover:text-fg",
                active === c.n && "border-primary text-fg",
              )}
            >
              {showNumbers ? <span className="text-primary-ink">{c.n}</span> : null}
              <Icon name={sourceIcon(c)} className="size-3.5 text-primary-ink" />
              <span className="truncate">{c.title}</span>
              {c.location ? <span className="font-medium text-fg-subtle">· {c.location}</span> : null}
            </button>
          </li>
        ))}
      </ul>
      <SourceSnippet id={snippetPanelId} citation={citations.find((c) => c.n === active)} detailed={detailed} labels={snippetLabels} />
    </div>
  );
}
